<template>
  <!-- INPUT FIELDS -->
  <div
    v-if="validFieldTypes.includes(field.type)"
    @click="field.getter && $emit('callGetter', field.getter)"
  >
    <!-- FIELD LABEL -->
    <label :for="field.id ? field.id : field.name" :class="[
      { 'text--danger' : field.error },
      { 'text--underline' : field.style && field.style.underline},
      { 'text--bold' : field.style && field.style.bold},
      { 'text--italic' : field.style && field.style.italic}
    ]" v-text="field.label"></label>
    <span class="field-required" v-if="field.required" v-text="'*'"></span>

    <!-- SELECT FIELDS -->
    <template v-if="field.type === 'select'">
      <v-select
        :options="field.options"
        v-model="field.value"
        :reduce="item => item.code"
        :disabled="disabled"
        @input="$emit('changes', field)"
        :class="`field__input--${field.type}`"
        :multiple="field.multiple"
      />
      <button 
        class="btn btn--transparent mt--2"
        v-text="'Clear Selection'"
        v-if="field.value.length > 5"
        @click="field.value = []"
      >
      </button>
    </template>

    <!-- DATE FIELDS -->
    <template v-else-if="field.type === 'date' || field.type === 'datetime'">
      <div class="field">
        <datepicker
          v-model="field.value"
          :disabled="disabled"
          :class="`field__input--${field.type}`"
        />
        <button
          @click.prevent="field.value = ''"
          :disabled="disabled"
          class="btn btn--icon btn--transparent ml--1"
        >
          <i class="material-icons" v-text="'close'"></i>
        </button>
      </div>
    </template>

    <!-- RANGE FIELDS -->
    <template v-else-if="field.type === 'range'">
      <div class="slider">
        <div class="slider__content">
          <span class="mr--2" v-text="field.slider.min"></span>
          <input
            id="slider"
            type="range"
            v-model="field.value"
            :step="field.slider.step"
            :min="field.slider.min"
            :max="field.slider.max"
            :disabled="disabled"
          />
          <span class="ml--2" v-text="field.slider.max"></span>
        </div>
        <label for="slider" v-text="field.value"></label>
      </div>
    </template>

    <!-- RADIO / CHECKBOX FIELDS -->
    <template v-else-if="field.type === 'radio' || field.type === 'checkbox'">
      <div id="radio-container" :class="{ 'fd--col' : field.options.length > 6}" class="d--flex mt--2" v-if="field.options && field.options.length">
        <div
          v-for="(option, key) in field.options"
          :key="key"
          class="mr--2"
        >

          <input
            :id="field.id + key"
            :type="field.type"
            :class="`field__input--${field.type}`"
            :placeholder="field.placeholder"
            :name="field.id"
            :disabled="disabled"
            :value="option"
            v-model="field.value"
          >
          <label :for="field.id + key" v-text="option" />
        </div>
      </div>

      <input
        v-else
        :id="field.id ? field.id : field.name"
        :type="field.type"
        v-model="field.value"
        :class="`field__input--${field.type}`"
        :placeholder="field.placeholder"
        :name="field.name"
        :disabled="disabled"
      >
    </template>

    <!-- TEXTAREA FIELDS -->
    <template v-else-if="field.type === 'textarea'">
      <textarea
        :id="field.id ? field.id : field.name"
        type="text"
        v-model="field.value"
        :class="`field__input--${field.type}`"
        :placeholder="field.placeholder"
        :name="field.name"
        :disabled="disabled"
      >
      </textarea>
    </template>

    <!-- TEXT FIELDS -->
    <template v-else>
      <input
        :id="field.id ? field.id : field.name"
        :type="field.type"
        v-model="field.value"
        :class="`field__input--${field.type}`"
        :placeholder="field.placeholder"
        :name="field.name"
        :disabled="disabled"
      >
    </template>
  </div>

  <!-- DESIGN FIELDS -->
  <div v-else-if="designFieldTypes.includes(field.type)">

    <!-- HEADER -->
    <template v-if="field.type === 'header'">
      <h2 
        v-if="field.size === 'Large'"
        v-text="field.content" 
        class="m--0"
        :class="[
          { 'text--underline' : field.style && field.style.underline},
          { 'text--bold' : field.style && field.style.bold},
          { 'text--italic' : field.style && field.style.italic}
        ]"
      ></h2>
      <h3 
        v-if="field.size === 'Medium'"
        v-text="field.content" 
        class="m--0"
        :class="[
          { 'text--underline' : field.style && field.style.underline},
          { 'text--bold' : field.style && field.style.bold},
          { 'text--italic' : field.style && field.style.italic}
        ]"
      ></h3>
      <h4 
        v-if="field.size === 'Small'"
        v-text="field.content" 
        class="m--0"
        :class="[
          { 'text--underline' : field.style && field.style.underline},
          { 'text--bold' : field.style && field.style.bold},
          { 'text--italic' : field.style && field.style.italic}
        ]"
      ></h4>
    </template>

    <!-- DESCRIPTION -->
    <template v-if="field.type === 'description'">
      <p
        v-text="field.content" 
        class="m--0" 
        :class="[
          { 'text--underline' : field.style && field.style.underline},
          { 'text--bold' : field.style && field.style.bold},
          { 'text--italic' : field.style && field.style.italic}
        ]"
      ></p>
    </template>

  </div>
</template>

<script>
import vSelect from 'vue-select'
import Datepicker from 'vuejs-datepicker'


  export default {
    name: 'field',
    components: {
      vSelect,
      Datepicker
    },
    props: {
      field: {
        type: Object,
        required: true
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    data: () => ({
      validFieldTypes: [
        'text',
        'textarea',
        'email',
        'number',
        'checkbox',
        'select',
        'date',
        'datetime',
        'radio',
        'range'
      ],
      designFieldTypes: [
        'header',
        'description'
      ]
    })
  }
</script>

<style lang="scss" scoped>

  @import '../scss/variables';
  @import '../scss/date-picker';

  #radio-container {
    @media screen and (max-width: 769px) {
      flex-direction: column;
    }
  }

  .fd {
    &--col {
      flex-direction: column;
    }
  }

  .text {
    &--underline {
      text-decoration: underline;
    }
    &--bold {
      font-weight: bold;
    }
    &--italic {
      font-style: italic;
    }
    &--success {
      color: $success;
    }
    &--danger {
      color: $danger;
    }
  }

  label {
    min-width: unset;
    margin-right: 0;
  }
  
  .slider {
    text-align: center;

    &__content {
      display: flex;
      align-items: center;

      #slider {
        width: 100%;
      }
    }
    label {
      margin-left: -10px;
    }
  }

  .field {
    display: flex;
    align-items: center;

    &-col {
      flex-direction: column;
    }

    &-required {
      font-weight: bold;
      margin-left: 5px;
    }

    &__input {
      &--text,
      &--textarea,
      &--number,
      &--email {
        width: 100%;
        max-width: -webkit-fill-available;
        background: $white;
        border: solid 1px rgba($black, 0.2);
        padding: 10px;
        border-radius: 5px;
        color: $black;
    
        &:disabled {
          background: $field-disabled;
          cursor: not-allowed;
        }
      }
    
      &--checkbox {
        flex-direction: row;
        align-items: center;
        width: 15px;
        height: 15px;
        margin-left: 10px;
        vertical-align: middle;
      }
    
      &--select {
        .vs__dropdown-toggle,
        input {
          padding: 3px;
          cursor: pointer;
        }
      }
    
      &--date {
        input {
          background: $white;
          border: solid 1px rgba($black, 0.2);
          padding: 10px;
          border-radius: 5px;
          color: $black;
          cursor: pointer;
      
          &:disabled {
            background: $field-disabled;
            cursor: not-allowed;
          }
        }
      }
    }
  }

  

</style>